import { useState, useMemo } from "react";
import Layout from "@/components/feature/Layout";
import { alacaklarData, type AlacakRecord } from "@/mocks/alacaklar";
import { musterilerData } from "@/mocks/musteriler";

function formatCurrency(value: number, currency = "₺"): string {
  return `${value.toLocaleString("tr-TR")} ${currency}`;
}

const durumColors: Record<string, string> = {
  Bekliyor: "bg-gray-500/10 text-gray-400",
  Yaklaştı: "bg-gold-500/10 text-gold-400",
  Gecikti: "bg-red-500/10 text-red-400",
  Ödendi: "bg-emerald-500/10 text-emerald-400",
};

const durumOptions = ["Tümü", "Bekliyor", "Yaklaştı", "Gecikti", "Ödendi"];

function WhatsAppLink({ record }: { record: AlacakRecord }) {
  const cleanPhone = record.telefon.replace(/\s/g, "");
  const message = `Merhaba ${record.adSoyad}, kuyum alışverişinizle ilgili kalan ödemeniz ${formatCurrency(record.kalanTutar)} olarak görünmektedir. Müsait olduğunuzda bizimle iletişime geçmenizi rica ederiz.`;
  return (
    <a
      href={`https://wa.me/${cleanPhone}?text=${encodeURIComponent(message)}`}
      target="_blank"
      rel="noopener noreferrer"
      className="w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 transition-colors cursor-pointer"
      title="WhatsApp Gönder"
    >
      <i className="ri-whatsapp-line" />
    </a>
  );
}

export default function Alacaklar() {
  const [search, setSearch] = useState("");
  const [durumFilter, setDurumFilter] = useState("Tümü");
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [selectedMusteri, setSelectedMusteri] = useState<number | "">("");
  const [selectedEditMusteri, setSelectedEditMusteri] = useState<number | "">("");

  const [data, setData] = useState<AlacakRecord[]>(alacaklarData);

  const filtered = useMemo(() => {
    return data.filter((item) => {
      const matchesSearch =
        item.adSoyad.toLowerCase().includes(search.toLowerCase()) ||
        item.telefon.includes(search) ||
        item.not.toLowerCase().includes(search.toLowerCase());
      const matchesDurum = durumFilter === "Tümü" || item.durum === durumFilter;
      return matchesSearch && matchesDurum;
    });
  }, [data, search, durumFilter]);

  const totalKalan = filtered.reduce((sum, item) => sum + item.kalanTutar, 0);
  const totalOdenen = filtered.reduce((sum, item) => sum + item.odenenTutar, 0);

  const musteriMap = useMemo(() => {
    const map: Record<number, { adSoyad: string; telefon: string }> = {};
    musterilerData.forEach((m) => {
      map[m.id] = { adSoyad: m.adSoyad, telefon: m.telefon };
    });
    return map;
  }, []);

  const handleMusteriChange = (musteriId: number | "", formType: "add" | "edit") => {
    if (formType === "add") {
      setSelectedMusteri(musteriId);
    } else {
      setSelectedEditMusteri(musteriId);
    }
  };

  const handleAdd = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;
    const fd = new FormData(form);
    const toplam = Number(fd.get("toplamBorc"));
    const odenen = Number(fd.get("odenenTutar"));
    const yeni: AlacakRecord = {
      id: Date.now(),
      adSoyad: String(fd.get("adSoyad") || ""),
      telefon: String(fd.get("telefon") || ""),
      toplamBorc: toplam,
      odenenTutar: odenen,
      kalanTutar: toplam - odenen,
      odemeTarihi: String(fd.get("odemeTarihi") || ""),
      durum: (fd.get("durum") as AlacakRecord["durum"]) || "Bekliyor",
      not: String(fd.get("not") || ""),
    };
    setData((prev) => [yeni, ...prev]);
    setShowAddForm(false);
    setSelectedMusteri("");
    form.reset();
  };

  const handleEdit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!editingId) return;
    const form = e.currentTarget as HTMLFormElement;
    const fd = new FormData(form);
    const toplam = Number(fd.get("toplamBorc"));
    const odenen = Number(fd.get("odenenTutar"));
    setData((prev) =>
      prev.map((item) =>
        item.id === editingId
          ? {
              ...item,
              adSoyad: String(fd.get("adSoyad") || ""),
              telefon: String(fd.get("telefon") || ""),
              toplamBorc: toplam,
              odenenTutar: odenen,
              kalanTutar: toplam - odenen,
              odemeTarihi: String(fd.get("odemeTarihi") || ""),
              durum: (fd.get("durum") as AlacakRecord["durum"]) || item.durum,
              not: String(fd.get("not") || ""),
            }
          : item
      )
    );
    setEditingId(null);
    setSelectedEditMusteri("");
    form.reset();
  };

  const handleDelete = (id: number) => {
    setData((prev) => prev.filter((item) => item.id !== id));
  };

  const editRecord = data.find((d) => d.id === editingId);

  return (
    <Layout>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-100 mb-1">Müşteri Alacak Takibi</h1>
        <p className="text-sm text-gray-500">Müşterilerden alınacak ödemeleri takip edin ve yönetin.</p>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="glass-card p-4">
          <p className="text-xs text-gray-500 mb-1">Toplam Alacak (Görünen)</p>
          <p className="text-xl font-semibold text-gold-400">{formatCurrency(filtered.reduce((s, i) => s + i.toplamBorc, 0))}</p>
        </div>
        <div className="glass-card p-4">
          <p className="text-xs text-gray-500 mb-1">Toplam Ödenen</p>
          <p className="text-xl font-semibold text-emerald-400">{formatCurrency(totalOdenen)}</p>
        </div>
        <div className="glass-card p-4">
          <p className="text-xs text-gray-500 mb-1">Toplam Kalan</p>
          <p className="text-xl font-semibold text-red-400">{formatCurrency(totalKalan)}</p>
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-lg px-3 py-2">
            <i className="ri-search-line text-gray-500 text-sm" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Müşteri ara..."
              className="bg-transparent text-sm text-gray-300 placeholder-gray-500 outline-none w-48"
            />
          </div>
          <select
            value={durumFilter}
            onChange={(e) => setDurumFilter(e.target.value)}
            className="input-dark"
          >
            {durumOptions.map((d) => (
              <option key={d} value={d} className="bg-dark-950">
                {d}
              </option>
            ))}
          </select>
        </div>
        <button onClick={() => setShowAddForm(true)} className="btn-gold">
          <i className="ri-add-line" />
          Yeni Alacak Ekle
        </button>
      </div>

      {/* Table */}
      <div className="glass-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/5">
                <th className="text-left text-xs text-gray-500 font-medium px-4 py-3">Ad Soyad</th>
                <th className="text-left text-xs text-gray-500 font-medium px-4 py-3">Telefon</th>
                <th className="text-right text-xs text-gray-500 font-medium px-4 py-3">Toplam</th>
                <th className="text-right text-xs text-gray-500 font-medium px-4 py-3">Ödenen</th>
                <th className="text-right text-xs text-gray-500 font-medium px-4 py-3">Kalan</th>
                <th className="text-left text-xs text-gray-500 font-medium px-4 py-3">Son Tarih</th>
                <th className="text-left text-xs text-gray-500 font-medium px-4 py-3">Durum</th>
                <th className="text-left text-xs text-gray-500 font-medium px-4 py-3">Not</th>
                <th className="text-right text-xs text-gray-500 font-medium px-4 py-3">İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((item) => (
                <tr key={item.id} className="table-row-hover border-b border-white/5 last:border-0">
                  <td className="px-4 py-3 text-sm text-gray-200 font-medium">{item.adSoyad}</td>
                  <td className="px-4 py-3 text-sm text-gray-400">{item.telefon}</td>
                  <td className="px-4 py-3 text-sm text-gray-300 text-right">{formatCurrency(item.toplamBorc)}</td>
                  <td className="px-4 py-3 text-sm text-emerald-400 text-right">{formatCurrency(item.odenenTutar)}</td>
                  <td className="px-4 py-3 text-sm text-red-400 text-right font-medium">{formatCurrency(item.kalanTutar)}</td>
                  <td className="px-4 py-3 text-sm text-gray-400">{item.odemeTarihi}</td>
                  <td className="px-4 py-3">
                    <span className={`status-badge ${durumColors[item.durum] || ""}`}>{item.durum}</span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500 max-w-[200px] truncate">{item.not}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <WhatsAppLink record={item} />
                      <button
                        onClick={() => setEditingId(item.id)}
                        className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/5 text-gray-400 hover:bg-white/10 transition-colors cursor-pointer"
                        title="Düzenle"
                      >
                        <i className="ri-edit-line" />
                      </button>
                      <button
                        onClick={() => handleDelete(item.id)}
                        className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/5 text-gray-400 hover:bg-red-500/10 hover:text-red-400 transition-colors cursor-pointer"
                        title="Sil"
                      >
                        <i className="ri-delete-bin-line" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && (
          <div className="p-8 text-center text-gray-500 text-sm">
            Kayıt bulunamadı.
          </div>
        )}
      </div>

      {/* Add Modal */}
      {showAddForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="glass-card w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-white/5">
              <h3 className="text-lg font-semibold text-gray-100">Yeni Alacak Ekle</h3>
              <button onClick={() => setShowAddForm(false)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-200 cursor-pointer">
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <form onSubmit={handleAdd} className="p-4 space-y-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1.5">Müşteri Rehberi</label>
                <select
                  value={selectedMusteri}
                  onChange={(e) => {
                    const val = e.target.value === "" ? "" : Number(e.target.value);
                    handleMusteriChange(val, "add");
                  }}
                  className="input-dark w-full"
                >
                  <option value="" className="bg-dark-950">Manuel gir veya rehberden seç...</option>
                  {musterilerData.map((m) => (
                    <option key={m.id} value={m.id} className="bg-dark-950">
                      {m.adSoyad} — {m.telefon}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1.5">Ad Soyad</label>
                <input
                  name="adSoyad"
                  required
                  className="input-dark w-full"
                  placeholder="Müşteri adı"
                  value={selectedMusteri !== "" ? musteriMap[selectedMusteri]?.adSoyad || "" : undefined}
                  onChange={() => {}}
                  readOnly={selectedMusteri !== ""}
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1.5">Telefon</label>
                <input
                  name="telefon"
                  required
                  className="input-dark w-full"
                  placeholder="+90 555 123 4567"
                  value={selectedMusteri !== "" ? musteriMap[selectedMusteri]?.telefon || "" : undefined}
                  onChange={() => {}}
                  readOnly={selectedMusteri !== ""}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1.5">Toplam Borç</label>
                  <input name="toplamBorc" type="number" required min={0} className="input-dark w-full" placeholder="0" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1.5">Ödenen Tutar</label>
                  <input name="odenenTutar" type="number" required min={0} defaultValue={0} className="input-dark w-full" placeholder="0" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1.5">Ödeme Tarihi</label>
                  <input name="odemeTarihi" type="date" required className="input-dark w-full" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1.5">Durum</label>
                  <select name="durum" className="input-dark w-full" defaultValue="Bekliyor">
                    <option value="Bekliyor" className="bg-dark-950">Bekliyor</option>
                    <option value="Yaklaştı" className="bg-dark-950">Yaklaştı</option>
                    <option value="Gecikti" className="bg-dark-950">Gecikti</option>
                    <option value="Ödendi" className="bg-dark-950">Ödendi</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1.5">Not</label>
                <textarea name="not" rows={3} className="input-dark w-full resize-none" placeholder="Not ekle..." />
              </div>
              <div className="flex items-center justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowAddForm(false)} className="btn-dark">İptal</button>
                <button type="submit" className="btn-gold">Kaydet</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingId && editRecord && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="glass-card w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-white/5">
              <h3 className="text-lg font-semibold text-gray-100">Alacak Düzenle</h3>
              <button onClick={() => setEditingId(null)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-200 cursor-pointer">
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <form onSubmit={handleEdit} className="p-4 space-y-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1.5">Müşteri Rehberi</label>
                <select
                  value={selectedEditMusteri}
                  onChange={(e) => {
                    const val = e.target.value === "" ? "" : Number(e.target.value);
                    handleMusteriChange(val, "edit");
                  }}
                  className="input-dark w-full"
                >
                  <option value="" className="bg-dark-950">Mevcut müşteri veya rehberden değiştir...</option>
                  {musterilerData.map((m) => (
                    <option key={m.id} value={m.id} className="bg-dark-950">
                      {m.adSoyad} — {m.telefon}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1.5">Ad Soyad</label>
                <input
                  name="adSoyad"
                  required
                  className="input-dark w-full"
                  defaultValue={editRecord.adSoyad}
                  value={selectedEditMusteri !== "" ? musteriMap[selectedEditMusteri]?.adSoyad || "" : undefined}
                  onChange={() => {}}
                  readOnly={selectedEditMusteri !== ""}
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1.5">Telefon</label>
                <input
                  name="telefon"
                  required
                  className="input-dark w-full"
                  defaultValue={editRecord.telefon}
                  value={selectedEditMusteri !== "" ? musteriMap[selectedEditMusteri]?.telefon || "" : undefined}
                  onChange={() => {}}
                  readOnly={selectedEditMusteri !== ""}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1.5">Toplam Borç</label>
                  <input name="toplamBorc" type="number" defaultValue={editRecord.toplamBorc} required min={0} className="input-dark w-full" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1.5">Ödenen Tutar</label>
                  <input name="odenenTutar" type="number" defaultValue={editRecord.odenenTutar} required min={0} className="input-dark w-full" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1.5">Ödeme Tarihi</label>
                  <input name="odemeTarihi" type="date" defaultValue={editRecord.odemeTarihi} required className="input-dark w-full" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1.5">Durum</label>
                  <select name="durum" className="input-dark w-full" defaultValue={editRecord.durum}>
                    <option value="Bekliyor" className="bg-dark-950">Bekliyor</option>
                    <option value="Yaklaştı" className="bg-dark-950">Yaklaştı</option>
                    <option value="Gecikti" className="bg-dark-950">Gecikti</option>
                    <option value="Ödendi" className="bg-dark-950">Ödendi</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1.5">Not</label>
                <textarea name="not" rows={3} defaultValue={editRecord.not} className="input-dark w-full resize-none" />
              </div>
              <div className="flex items-center justify-end gap-3 pt-2">
                <button type="button" onClick={() => setEditingId(null)} className="btn-dark">İptal</button>
                <button type="submit" className="btn-gold">Güncelle</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
